(() => {
  const money = (cents, format) => {
    const value = Number(cents || 0);
    if (window.Shopify && typeof Shopify.formatMoney === 'function') return Shopify.formatMoney(value, format || undefined);
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(value / 100);
  };

  class AirGuardProduct extends HTMLElement {
    connectedCallback() {
      this.moneyFormat = this.dataset.moneyFormat;
      this.variants = this.readJson('[data-airguard-variants]');
      this.selectedOptions = [...(this.getInitialVariant()?.options || [])];
      this.form = this.querySelector('[data-airguard-main-form]') || this.querySelector('form[action*="/cart/add"]:not([data-airguard-package-form])');
      this.idInput = this.form?.querySelector('input[name="id"]');
      this.quantityInput = this.form?.querySelector('input[name="quantity"]');
      this.addButton = this.form?.querySelector('[data-airguard-atc]');
      this.sticky = this.querySelector('[data-airguard-sticky]');
      this.bindGallery();
      this.bindOptions();
      this.bindQuantity();
      this.bindPackages();
      this.bindPackageForms();
      this.bindReviews();
      this.bindFaq();
      this.bindReveal();
      this.bindTimer();
      this.bindSticky();
      this.updateVariantState(this.findVariant(), { updateUrl: false, syncOptions: true });
    }

    readJson(selector) {
      try { return JSON.parse(this.querySelector(selector)?.textContent || '[]'); } catch (error) { return []; }
    }

    getInitialVariant() {
      const id = Number(this.dataset.currentVariantId || this.idInput?.value);
      return this.variants.find((variant) => Number(variant.id) === id) || this.variants.find((variant) => variant.available) || this.variants[0];
    }

    bindGallery() {
      this.querySelectorAll('[data-airguard-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => this.activateMedia(thumb.dataset.mediaId));
      });
    }

    activateMedia(mediaId) {
      if (!mediaId) return;
      this.querySelectorAll('[data-airguard-media]').forEach((media) => {
        const active = String(media.dataset.mediaId) === String(mediaId);
        media.classList.toggle('is-active', active);
        media.hidden = !active;
      });
      this.querySelectorAll('[data-airguard-thumb]').forEach((thumb) => {
        const active = String(thumb.dataset.mediaId) === String(mediaId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    bindOptions() {
      this.querySelectorAll('[data-airguard-option-value]').forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;
          const position = Number(button.dataset.optionPosition);
          this.selectedOptions[position] = button.dataset.optionValue;
          this.updateVariantState(this.findVariant(), { updateUrl: true, syncOptions: true });
        });
      });
    }

    findVariant() {
      return this.variants.find((variant) => variant.options?.every((value, index) => String(value) === String(this.selectedOptions[index])))
        || this.variants.find((variant) => variant.available)
        || this.getInitialVariant();
    }

    updateVariantState(variant, options = {}) {
      if (!variant) return;
      this.selectedOptions = [...(variant.options || this.selectedOptions)];
      if (this.idInput) {
        this.idInput.value = variant.id;
        this.idInput.disabled = !variant.available;
      }
      this.updatePrices(variant);
      this.updateAvailability(variant);
      this.updateOptionButtons();
      this.updatePackageFromVariant(variant);
      this.updateMediaFromVariant(variant);
      if (options.updateUrl) this.updateUrl(variant);
      if (options.syncOptions) this.syncOptionButtonStates();
    }

    syncOptionButtonStates() {
      this.querySelectorAll('[data-airguard-option-value]').forEach((button) => {
        const position = Number(button.dataset.optionPosition);
        const selected = String(this.selectedOptions[position]) === String(button.dataset.optionValue);
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    updateOptionButtons() {
      this.querySelectorAll('[data-airguard-option-value]').forEach((button) => {
        const position = Number(button.dataset.optionPosition);
        const test = [...this.selectedOptions];
        test[position] = button.dataset.optionValue;
        const available = this.variants.some((variant) => variant.available && variant.options?.every((value, index) => String(value) === String(test[index])));
        button.disabled = !available;
        button.classList.toggle('is-disabled', !available);
      });
    }

    updatePrices(variant) {
      const price = Number(variant.price || 0);
      const compare = Number(variant.compare_at_price || 0);
      this.querySelectorAll('[data-airguard-price]').forEach((el) => { el.textContent = money(price, this.moneyFormat); });
      this.querySelectorAll('[data-airguard-compare]').forEach((el) => {
        el.textContent = compare > price ? money(compare, this.moneyFormat) : '';
        el.hidden = !(compare > price);
      });
      const percent = compare > price && compare > 0 ? Math.round(((compare - price) * 100) / compare) : 0;
      this.querySelectorAll('[data-airguard-save]').forEach((el) => {
        el.hidden = percent <= 0;
        if (percent > 0) el.textContent = (el.dataset.template || 'SAVE [percent]%').replace('[percent]', percent);
      });
      this.querySelectorAll('[data-airguard-sticky-price]').forEach((el) => { el.textContent = money(price, this.moneyFormat); });
    }

    updateAvailability(variant) {
      if (!this.addButton) return;
      const label = this.addButton.querySelector('[data-airguard-atc-label]');
      this.addButton.disabled = !variant.available;
      if (label) label.textContent = variant.available ? (this.addButton.dataset.labelAvailable || 'Add to cart') : (this.addButton.dataset.labelSoldOut || 'Sold out');
    }

    updatePackageFromVariant(variant) {
      const title = `${variant.title || ''} ${variant.options?.join(' ') || ''}`.toLowerCase();
      let matched = false;
      this.querySelectorAll('[data-airguard-package]').forEach((card) => {
        const value = (card.dataset.packValue || '').toLowerCase();
        const active = value && title.includes(value.toLowerCase());
        if (active) matched = true;
        card.classList.toggle('is-selected', active);
        card.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      if (!matched) this.querySelector('[data-airguard-package]')?.classList.add('is-selected');
    }

    updateMediaFromVariant(variant) {
      const mediaId = variant.featured_media?.id || variant.featured_image?.id;
      if (mediaId) this.activateMedia(mediaId);
    }

    updateUrl(variant) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    bindQuantity() {
      if (!this.quantityInput) return;
      this.querySelector('[data-airguard-qty-minus]')?.addEventListener('click', () => this.changeQuantity(-1));
      this.querySelector('[data-airguard-qty-plus]')?.addEventListener('click', () => this.changeQuantity(1));
      this.quantityInput.addEventListener('change', () => {
        const min = Number(this.quantityInput.min || 1);
        const value = Math.max(min, Number(this.quantityInput.value || min));
        this.quantityInput.value = String(value);
      });
    }

    changeQuantity(delta) {
      const min = Number(this.quantityInput.min || 1);
      const step = Number(this.quantityInput.step || 1);
      const next = Math.max(min, Number(this.quantityInput.value || min) + delta * step);
      this.quantityInput.value = String(next);
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    bindPackages() {
      this.querySelectorAll('[data-airguard-package]').forEach((card) => {
        card.addEventListener('click', () => this.selectPackage(card));
      });
    }

    selectPackage(card) {
      this.querySelectorAll('[data-airguard-package]').forEach((item) => item.classList.toggle('is-selected', item === card));
      const packValue = card.dataset.packValue;
      if (packValue) {
        const matchingVariant = this.variants.find((variant) => variant.available && variant.options?.some((value) => String(value).toLowerCase() === packValue.toLowerCase()))
          || this.variants.find((variant) => variant.options?.some((value) => String(value).toLowerCase() === packValue.toLowerCase()));
        if (matchingVariant) this.updateVariantState(matchingVariant, { updateUrl: true, syncOptions: true });
      }
      const quantity = Number(card.dataset.packQuantity || 1);
      if (this.quantityInput && quantity > 0) {
        this.quantityInput.value = String(quantity);
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    bindPackageForms() {
      this.querySelectorAll('[data-airguard-package-form]').forEach((form) => {
        form.addEventListener('submit', async (event) => {
          if (!window.fetch || !window.FormData) return;
          event.preventDefault();
          const button = form.querySelector('button[type="submit"]');
          if (button) {
            button.disabled = true;
            button.setAttribute('aria-busy', 'true');
          }
          try {
            const body = new FormData(form);
            body.delete('return_to');
            const response = await fetch(form.dataset.cartAddUrl || form.action, {
              method: 'POST',
              headers: { Accept: 'application/json' },
              body,
            });
            if (!response.ok) throw new Error('Unable to add AirGuard package to cart');
            window.dispatchEvent(new CustomEvent('smartclaw:add_to_cart', {
              detail: {
                productId: Number(form.dataset.productId || 0),
                variantId: Number(form.dataset.variantId || 0),
                quantity: Number(form.dataset.quantity || 1),
                amount: Number(form.dataset.priceCents || 0) / 100,
                currency: window.Shopify?.currency?.active || '',
              },
            }));
            window.location.href = form.dataset.redirect || '/cart';
          } catch (error) {
            HTMLFormElement.prototype.submit.call(form);
          }
        });
      });
    }

    bindReviews() {
      const track = this.querySelector('[data-airguard-reviews-track]');
      if (track) {
        const cards = Array.from(track.children);
        const viewport = track.closest('.sp1-rcarousel') || track.parentElement;
        let index = 0;
        let scrollFrame = null;
        const setActiveReview = (nextIndex, shouldScroll = false) => {
          index = Math.max(0, Math.min(cards.length - 1, nextIndex));
          cards.forEach((card, cardIndex) => card.classList.toggle('is-active', cardIndex === index));
          this.querySelectorAll('[data-airguard-review-dot]').forEach((dot, dotIndex) => dot.classList.toggle('sp1-on', dotIndex === index));
          const target = cards[index];
          if (shouldScroll && viewport && target && typeof viewport.scrollTo === 'function') {
            viewport.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
          }
        };
        const syncActiveFromScroll = () => {
          if (!viewport || !cards.length) return;
          const viewportCenter = viewport.scrollLeft + viewport.clientWidth / 2;
          const nextIndex = cards.reduce((closestIndex, card, cardIndex) => {
            const cardCenter = card.offsetLeft + card.offsetWidth / 2;
            const closestCard = cards[closestIndex];
            const closestCenter = closestCard.offsetLeft + closestCard.offsetWidth / 2;
            return Math.abs(cardCenter - viewportCenter) < Math.abs(closestCenter - viewportCenter) ? cardIndex : closestIndex;
          }, 0);
          if (nextIndex !== index) setActiveReview(nextIndex, false);
        };
        viewport?.addEventListener('scroll', () => {
          if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
          scrollFrame = window.requestAnimationFrame(syncActiveFromScroll);
        }, { passive: true });
        window.addEventListener('resize', syncActiveFromScroll);
        this.querySelector('[data-airguard-review-prev]')?.addEventListener('click', () => { setActiveReview((index - 1 + cards.length) % cards.length, true); });
        this.querySelector('[data-airguard-review-next]')?.addEventListener('click', () => { setActiveReview((index + 1) % cards.length, true); });
        this.querySelectorAll('[data-airguard-review-dot]').forEach((dot, dotIndex) => dot.addEventListener('click', () => { setActiveReview(dotIndex, true); }));
        setActiveReview(0, false);
      }
      const load = this.querySelector('[data-airguard-load-comments]');
      load?.addEventListener('click', () => {
        const hidden = Array.from(this.querySelectorAll('[data-airguard-comment].is-hidden'));
        hidden.slice(0, 4).forEach((comment) => comment.classList.remove('is-hidden'));
        if (!this.querySelector('[data-airguard-comment].is-hidden')) load.classList.add('is-hidden');
      });
    }

    bindFaq() {
      this.querySelectorAll('.sp1-acc details').forEach((detail) => {
        detail.addEventListener('toggle', () => {
          const icon = detail.querySelector('.sp1-pm');
          if (icon) icon.textContent = detail.open ? '×' : '+';
        });
      });
    }

    bindReveal() {
      const items = this.querySelectorAll('.sp1-rv');
      if (!items.length) return;
      if (!('IntersectionObserver' in window)) {
        items.forEach((item) => item.classList.add('sp1-in'));
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('sp1-in');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12 });
      items.forEach((item) => observer.observe(item));
    }

    bindTimer() {
      const nodes = this.querySelectorAll('[data-airguard-timer]');
      if (!nodes.length) return;
      let remaining = Number(this.dataset.timerSeconds || 7920);
      const render = () => {
        const hours = Math.floor(remaining / 3600);
        const minutes = Math.floor((remaining % 3600) / 60);
        const seconds = remaining % 60;
        const text = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        nodes.forEach((node) => { node.textContent = text; });
        remaining = Math.max(0, remaining - 1);
      };
      render();
      window.setInterval(render, 1000);
    }

    bindSticky() {
      const trigger = this.querySelector('#sp1-offer');
      if (!this.sticky || !trigger || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => this.sticky.classList.toggle('is-visible', !entry.isIntersecting));
      }, { threshold: 0 });
      observer.observe(trigger);
      this.querySelector('[data-airguard-sticky-button]')?.addEventListener('click', () => {
        this.querySelector('#sp1-offer')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  if (!customElements.get('airguard-product')) customElements.define('airguard-product', AirGuardProduct);
})();
